/*
 * Diagnóstico ecommerce · Enova
 * Lógica del quiz de sections/enova-diagnostico.liquid.
 * El contenido (preguntas, rutas y prioridades) vive en snippets/enova-dx-preguntas.liquid.
 *
 * Flujo: landing → pregunta 1 (define la ruta) → preguntas 2-5 de la ruta → contacto → resultado.
 * El lead se envía por fetch a la función de Supabase `diagnostico-lead` (settings.endpoint), que lo guarda en
 * la tabla leads y crea o actualiza el cliente en Shopify. No se usa el formulario nativo de Shopify porque su
 * filtro anti-spam (captcha y descartes silenciosos) bloqueaba los leads.
 * Si está configurado en la sección, también se envía a un formulario de HubSpot.
 */
(() => {
  if (customElements.get('enova-diagnostico')) return;

  const STATE_KEY = 'enova-dx-state-v1';
  const RESULT_KEY = 'enova-dx-result-v1';
  const ORIGIN_KEY = 'enova-dx-origin-v1';
  const START_HASH = '#empezar';
  const TRACKING_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'ttclid'];

  const store = {
    read(key) {
      try {
        const raw = window.sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    },
    write(key, value) {
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* almacenamiento no disponible: el quiz sigue funcionando sin persistencia */
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        /* noop */
      }
    },
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const format = (template, vars) => String(template ?? '').replace(/\{(\w+)\}/g, (match, key) => (key in vars ? vars[key] : match));

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const freshState = () => ({ v: 1, step: 0, answers: {}, started: false });

  class EnovaDiagnostico extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      try {
        this.config = JSON.parse(this.querySelector('[data-dx-config]').textContent);
        this.settings = JSON.parse(this.querySelector('[data-dx-settings]').textContent);
      } catch (error) {
        console.error('[Diagnóstico] No se pudo leer la configuración del quiz', error);
        return;
      }

      this.ready = true;
      this.designMode = this.dataset.designMode === 'true';
      this.total = this.config.total || 5;
      this.ui = this.config.ui || {};
      this.icons = this.readIcons();
      this.views = {};
      this.querySelectorAll('[data-dx-view]').forEach((view) => {
        this.views[view.dataset.dxView] = view;
      });
      this.stepsEl = this.querySelector('[data-dx-steps]');
      this.contactEl = this.querySelector('[data-dx-contact]');
      this.form = document.getElementById(this.dataset.formId);

      this.state = this.loadState();
      this.captureOrigin();
      this.bindEvents();

      const initial = this.dataset.state;
      if (initial === 'result') {
        this.showResult({ preview: true });
      } else if (initial === 'quiz') {
        this.showView('quiz');
        this.goTo(this.state.step, { scroll: false, focus: false });
      } else {
        this.refreshLanding();
        if (window.location.hash === START_HASH) this.start({ push: false });
      }
    }

    disconnectedCallback() {
      if (this.onPopState) window.removeEventListener('popstate', this.onPopState);
    }

    /* ---------- Estado y datos */

    loadState() {
      const saved = store.read(STATE_KEY);
      if (saved && saved.v === 1 && saved.answers && typeof saved.answers === 'object') return saved;
      return freshState();
    }

    saveState() {
      if (this.designMode) return;
      this.state.updated = Date.now();
      store.write(STATE_KEY, this.state);
    }

    get route() {
      const option = this.findOption(this.config.stage, this.state.answers[this.config.stage.id]);
      return option && this.config.routes[option.route] ? option.route : null;
    }

    routeConfig() {
      return this.route ? this.config.routes[this.route] : null;
    }

    questions() {
      const route = this.routeConfig();
      return [this.config.stage, ...(route ? route.questions : [])];
    }

    stepCount() {
      return this.route ? this.questions().length : this.total;
    }

    keyFor(question) {
      return question === this.config.stage ? question.id : `${this.route}.${question.id}`;
    }

    findOption(question, value) {
      return (question.options || []).find((option) => option.value === value);
    }

    answerText(question) {
      const value = this.state.answers[this.keyFor(question)];
      if (question.type === 'text') return String(value || '').trim();
      if (question.type === 'multi') {
        return (Array.isArray(value) ? value : [])
          .map((item) => this.findOption(question, item)?.title)
          .filter(Boolean)
          .join(', ');
      }
      return this.findOption(question, value)?.title || '';
    }

    stepError(question) {
      const value = this.state.answers[this.keyFor(question)];
      if (question.type === 'text') {
        const min = question.minLength || 1;
        return String(value || '').trim().length >= min ? '' : format(this.ui.requiredText, { min });
      }
      if (question.type === 'multi') {
        const valid = Array.isArray(value) && value.some((item) => this.findOption(question, item));
        return valid ? '' : this.ui.requiredMulti;
      }
      return this.findOption(question, value) ? '' : this.ui.requiredSingle;
    }

    firstIncomplete() {
      if (!this.route) return 0;
      const questions = this.questions();
      const index = questions.findIndex((question) => this.stepError(question));
      return index === -1 ? questions.length : index;
    }

    computePriorities(limit = 3) {
      const route = this.routeConfig();
      if (!route) return [];
      const catalog = this.config.priorities || {};
      const scores = new Map();
      let order = 0;
      const add = (key, weight) => {
        if (!catalog[key]) return;
        const current = scores.get(key) || { score: 0, order: order++ };
        current.score += weight;
        scores.set(key, current);
      };

      route.questions.forEach((question) => {
        const value = this.state.answers[this.keyFor(question)];
        const weight = question.weight || 1;
        if (question.type === 'single') {
          (this.findOption(question, value)?.priorities || []).forEach((key) => add(key, weight));
        } else if (question.type === 'multi') {
          const selected = Array.isArray(value) ? value : [];
          question.options.forEach((option) => {
            const keys = selected.includes(option.value) ? option.priorities : option.ifMissing;
            (keys || []).forEach((key) => add(key, weight));
          });
        }
      });
      (route.base || []).forEach((key, index) => add(key, 0.5 - index * 0.1));

      return [...scores.entries()]
        .sort((a, b) => b[1].score - a[1].score || a[1].order - b[1].order)
        .slice(0, limit)
        .map(([key]) => key);
    }

    captureOrigin() {
      if (this.designMode) return;
      const params = new URLSearchParams(window.location.search);
      const found = {};
      TRACKING_PARAMS.forEach((param) => {
        const value = params.get(param);
        if (value) found[param] = value.slice(0, 200);
      });
      const existing = store.read(ORIGIN_KEY);
      if (existing && !Object.keys(found).length) return;
      const referrer = document.referrer && !document.referrer.startsWith(window.location.origin) ? document.referrer.slice(0, 300) : '';
      store.write(ORIGIN_KEY, { ...found, referrer: referrer || existing?.referrer || '' });
    }

    describeOrigin() {
      const origin = store.read(ORIGIN_KEY) || {};
      const parts = TRACKING_PARAMS.filter((param) => origin[param]).map((param) => `${param}=${origin[param]}`);
      parts.push(`referrer=${origin.referrer || 'directo'}`);
      return parts.join(' | ');
    }

    leadFields() {
      const route = this.routeConfig();
      const fields = [['Perfil', route ? `${route.name} (${route.code})` : 'Sin definir']];
      this.questions().forEach((question) => fields.push([question.label, this.answerText(question) || '—']));
      const priorities = this.computePriorities().map((key) => this.config.priorities[key].title);
      if (priorities.length) fields.push(['Prioridades sugeridas', priorities.join(' · ')]);
      fields.push(['Origen', this.describeOrigin()]);
      return fields;
    }

    leadTags() {
      const slug = (value) =>
        String(value)
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      const tags = ['diagnostico'];
      if (this.route) tags.push(`diagnostico-${this.route}`);
      this.computePriorities().forEach((key) => tags.push(`prioridad-${key}`));

      // Respuestas de opción como etiquetas (dx-<pregunta>-<respuesta>) para crear segmentos en Clientes.
      if (this.route) {
        this.routeConfig().questions.forEach((question) => {
          if (question.type === 'text') return;
          const value = this.state.answers[this.keyFor(question)];
          (Array.isArray(value) ? value : [value])
            .filter((item) => this.findOption(question, item))
            .forEach((item) => tags.push(`dx-${slug(question.id)}-${slug(item)}`));
        });
      }
      return [...new Set(tags)];
    }

    /* ---------- Eventos */

    bindEvents() {
      this.addEventListener('click', (event) => {
        const target = event.target.closest('[data-dx-start], [data-dx-restart], [data-dx-next], [data-dx-back], [data-dx-edit]');
        if (!target || !this.contains(target)) return;
        if (target.hasAttribute('data-dx-start')) {
          event.preventDefault();
          this.start();
        } else if (target.hasAttribute('data-dx-restart')) {
          this.start({ restart: true });
        } else if (target.hasAttribute('data-dx-next')) {
          this.next();
        } else if (target.hasAttribute('data-dx-back')) {
          this.back();
        } else if (target.hasAttribute('data-dx-edit')) {
          this.goTo(Number(target.dataset.dxEdit));
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-dx-input]')) this.onAnswer(event.target);
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('textarea[data-dx-input]')) this.onAnswer(event.target);
        if (event.target.matches('[data-dx-field]')) this.validateField(event.target, { live: true });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && event.target.matches('textarea[data-dx-input]')) {
          event.preventDefault();
          this.next();
        }
      });

      if (this.form) {
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.form.querySelectorAll('[data-dx-field]').forEach((field) => {
          field.addEventListener('blur', () => {
            if (field.value || field.hasAttribute('aria-invalid')) this.validateField(field);
          });
        });
      }

      this.onPopState = () => {
        if (this.currentView === 'result') return;
        if (window.location.hash === START_HASH) {
          this.showView('quiz');
          this.goTo(this.state.step);
        } else if (this.currentView !== 'landing') {
          this.showView('landing');
          this.refreshLanding();
          this.scrollToTop();
        }
      };
      window.addEventListener('popstate', this.onPopState);
    }

    onAnswer(input) {
      const question = this.questions()[this.state.step];
      if (!question || this.state.step >= this.questions().length) return;
      const key = this.keyFor(question);

      if (question.type === 'multi') {
        this.state.answers[key] = [...this.stepsEl.querySelectorAll('[data-dx-input]:checked')].map((item) => item.value);
      } else {
        this.state.answers[key] = input.value;
      }

      if (question === this.config.stage) this.updateProgress();
      this.setStepError('');
      this.saveState();
    }

    /* ---------- Navegación */

    start({ push = true, restart = false } = {}) {
      if (restart) this.state = freshState();
      if (!this.state.started) {
        this.state.started = true;
        this.state.startedAt = Date.now();
        this.track('diagnostico_inicio');
      }
      this.saveState();
      this.showView('quiz');
      if (push && window.location.hash !== START_HASH) window.history.pushState({ dx: 'quiz' }, '', START_HASH);
      this.goTo(this.state.step || 0);
    }

    next() {
      const index = this.state.step;
      const question = this.questions()[index];
      if (!question) return;
      const error = this.stepError(question);
      if (error) {
        this.setStepError(error, { focus: question.type === 'text' });
        return;
      }
      this.track('diagnostico_paso', { paso: index + 1, pregunta: question.label, perfil: this.routeConfig()?.name || '' });
      this.goTo(index + 1);
    }

    back() {
      if (this.state.step <= 0) {
        this.exitToLanding();
        return;
      }
      this.goTo(this.state.step - 1);
    }

    exitToLanding() {
      if (window.history.state && window.history.state.dx === 'quiz') {
        window.history.back();
        return;
      }
      if (window.location.hash === START_HASH) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      this.showView('landing');
      this.refreshLanding();
      this.scrollToTop();
    }

    goTo(index, { scroll = true, focus = true } = {}) {
      this.state.step = Math.max(0, Math.min(Number(index) || 0, this.firstIncomplete()));
      this.saveState();

      if (this.state.step >= this.questions().length) {
        this.renderContact();
      } else {
        this.renderQuestion(this.state.step);
      }
      this.updateProgress();

      if (scroll) this.scrollToTop();
      if (focus) window.requestAnimationFrame(() => this.focusTitle());
    }

    showView(name) {
      Object.entries(this.views).forEach(([key, view]) => {
        view.hidden = key !== name;
      });
      this.currentView = name;
    }

    refreshLanding() {
      const inProgress = Boolean(this.state.started && (this.state.answers[this.config.stage.id] || this.state.step > 0));
      this.querySelectorAll('[data-dx-start-label]').forEach((label) => {
        label.textContent = inProgress ? this.ui.resume : label.dataset.default;
      });
      const restart = this.querySelector('[data-dx-restart]');
      if (restart) restart.hidden = !inProgress;
    }

    scrollToTop() {
      const top = Math.max(0, this.getBoundingClientRect().top + window.scrollY);
      if (window.scrollY <= top + 4) return;
      window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    focusTitle() {
      const title =
        this.state.step >= this.questions().length
          ? this.querySelector('[data-dx-contact-title]')
          : this.stepsEl.querySelector('.dx-step__title');
      title?.focus({ preventScroll: true });
    }

    /* ---------- Render */

    readIcons() {
      const icons = {};
      const template = this.querySelector('template[data-dx-icons]');
      template?.content.querySelectorAll('[data-icon]').forEach((item) => {
        icons[item.dataset.icon] = item.innerHTML;
      });
      return icons;
    }

    icon(name) {
      return this.icons[name] || '';
    }

    updateProgress() {
      const count = this.stepCount();
      const step = this.state.step;
      const isContact = this.route && step >= count;
      const pct = isContact ? 100 : Math.round(((step + 1) / count) * 100);
      const route = this.routeConfig();

      const countEl = this.querySelector('[data-dx-count]');
      const pctEl = this.querySelector('[data-dx-pct]');
      const profileEl = this.querySelector('[data-dx-profile]');
      const bar = this.querySelector('[data-dx-bar]');
      const fill = this.querySelector('[data-dx-fill]');

      if (countEl) countEl.textContent = isContact ? this.ui.contactCount : format(this.ui.questionCount, { n: step + 1, total: count });
      if (pctEl) pctEl.textContent = format(this.ui.progress, { pct });
      if (fill) fill.style.width = `${pct}%`;
      if (bar) bar.setAttribute('aria-valuenow', String(pct));
      if (profileEl) {
        profileEl.hidden = !(route && step > 0);
        profileEl.textContent = route ? format(this.ui.profile, { name: route.name }) : '';
      }
    }

    renderQuestion(index) {
      const question = this.questions()[index];
      const isStage = question === this.config.stage;
      const route = this.routeConfig();
      const eyebrow = isStage ? question.eyebrow : question.eyebrow || route?.eyebrow;
      const titleId = `${this.id}-title-${index}`;
      const errorId = `${this.id}-error-${index}`;
      const body =
        question.type === 'text'
          ? this.renderTextField(question, errorId)
          : this.renderOptions(question, isStage, titleId, errorId);

      this.contactEl.hidden = true;
      this.stepsEl.hidden = false;
      this.stepsEl.innerHTML = `
        <div class="dx-step" data-step="${index}">
          <div class="dx-step__head">
            ${!isStage && question.icon ? `<span class="dx-step__icon">${this.icon(question.icon)}</span>` : ''}
            ${eyebrow ? `<p class="dx-eyebrow">${escapeHtml(eyebrow)}</p>` : ''}
            <h2 class="dx-step__title" id="${titleId}" tabindex="-1">${escapeHtml(question.title)}</h2>
            ${question.description ? `<p class="dx-step__desc">${escapeHtml(question.description)}</p>` : ''}
            ${question.type === 'multi' ? `<p class="dx-step__hint">${escapeHtml(this.ui.multiHint)}</p>` : ''}
          </div>
          ${body}
          <p class="dx-error" id="${errorId}" role="alert" data-dx-step-error></p>
          <div class="dx-nav">
            <button type="button" class="dx-btn dx-btn--ghost" data-dx-back>
              ${this.icon('arrow-left')}<span>${escapeHtml(this.ui.back)}</span>
            </button>
            <button type="button" class="dx-btn dx-btn--primary" data-dx-next>
              <span>${escapeHtml(this.ui.next)}</span>${this.icon('arrow-right')}
            </button>
          </div>
        </div>`;
    }

    renderOptions(question, isStage, titleId, errorId) {
      const multi = question.type === 'multi';
      const value = this.state.answers[this.keyFor(question)];
      const selected = multi ? (Array.isArray(value) ? value : []) : [value];
      const layout = isStage || question.layout === 'rich' ? 'rich' : 'list';
      const name = `${this.id}-${this.keyFor(question)}`;

      const options = question.options
        .map((option) => {
          const routeName = option.route ? this.config.routes[option.route]?.name : '';
          return `
            <label class="dx-opt${multi ? ' dx-opt--multi' : ''}">
              <input class="dx-opt__input" type="${multi ? 'checkbox' : 'radio'}" name="${escapeHtml(name)}" value="${escapeHtml(option.value)}"${selected.includes(option.value) ? ' checked' : ''} data-dx-input>
              <span class="dx-opt__card">
                ${layout === 'rich' && option.icon ? `<span class="dx-opt__icon">${this.icon(option.icon)}</span>` : ''}
                <span class="dx-opt__mark" aria-hidden="true"></span>
                <span class="dx-opt__body">
                  <span class="dx-opt__title">${escapeHtml(option.title)}</span>
                  ${option.description ? `<span class="dx-opt__desc">${escapeHtml(option.description)}</span>` : ''}
                </span>
                ${routeName ? `<span class="dx-opt__tag">${escapeHtml(format(this.ui.route, { name: routeName }))}</span>` : ''}
              </span>
            </label>`;
        })
        .join('');

      return `<fieldset class="dx-options dx-options--${layout}" aria-labelledby="${titleId}" aria-describedby="${errorId}">${options}</fieldset>`;
    }

    renderTextField(question, errorId) {
      const inputId = `${this.id}-text-${question.id}`;
      const hintId = `${inputId}-hint`;
      const value = this.state.answers[this.keyFor(question)] || '';
      return `
        <div class="dx-textfield">
          <label class="dx-field__label" for="${inputId}">${escapeHtml(question.fieldLabel || question.title)}</label>
          <textarea class="dx-textarea" id="${inputId}" rows="4" maxlength="${Number(question.maxLength) || 600}" placeholder="${escapeHtml(question.placeholder || '')}" aria-describedby="${hintId} ${errorId}" data-dx-input>${escapeHtml(value)}</textarea>
          <p class="dx-step__hint" id="${hintId}">${escapeHtml(format(this.ui.textHint, { min: question.minLength || 1 }))}</p>
        </div>`;
    }

    setStepError(message, { focus = false } = {}) {
      const errorEl = this.stepsEl.querySelector('[data-dx-step-error]');
      if (errorEl) errorEl.textContent = message;
      const textarea = this.stepsEl.querySelector('textarea[data-dx-input]');
      if (textarea) {
        if (message) textarea.setAttribute('aria-invalid', 'true');
        else textarea.removeAttribute('aria-invalid');
        if (message && focus) textarea.focus();
      }
    }

    renderContact() {
      this.stepsEl.hidden = true;
      this.stepsEl.innerHTML = '';
      this.contactEl.hidden = false;

      const route = this.routeConfig();
      const questions = this.questions();
      const profile = this.querySelector('[data-dx-review-profile]');
      if (profile && route) {
        profile.textContent = `${format(this.ui.profile, { name: route.name })} · ${questions.length} respuestas`;
      }
      const list = this.querySelector('[data-dx-review]');
      if (list) {
        list.innerHTML = questions
          .map(
            (question, index) => `
              <li class="dx-review__item">
                <p class="dx-review__q">${escapeHtml(question.title)}</p>
                <p class="dx-review__a">${escapeHtml(this.answerText(question) || '—')}</p>
                <button type="button" class="dx-review__edit" data-dx-edit="${index}" aria-label="${escapeHtml(`${this.ui.edit}: ${question.title}`)}">${escapeHtml(this.ui.edit)}</button>
              </li>`
          )
          .join('');
      }
    }

    /* ---------- Formulario de contacto */

    fieldMessage(field) {
      const value = field.value.trim();
      switch (field.dataset.dxField) {
        case 'name':
          return value.length >= 2 ? '' : field.dataset.dxMsg;
        case 'email':
          return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) ? '' : field.dataset.dxMsg;
        case 'phone': {
          const digits = value.replace(/\D/g, '');
          return /^[+\d\s().-]+$/.test(value) && digits.length >= 7 && digits.length <= 15 ? '' : field.dataset.dxMsg;
        }
        case 'consent':
          return field.checked ? '' : field.dataset.dxMsg;
        default:
          return '';
      }
    }

    validateField(field, { live = false } = {}) {
      if (!field.dataset.dxField) return true;
      const message = this.fieldMessage(field);
      // Mientras se escribe solo se actualiza un error que ya estaba visible.
      if (live && !field.hasAttribute('aria-invalid')) return !message;

      const errorEl = document.getElementById(`${field.id}-error`);
      if (message) {
        field.setAttribute('aria-invalid', 'true');
        if (errorEl) errorEl.textContent = message;
      } else {
        field.removeAttribute('aria-invalid');
        if (errorEl) errorEl.textContent = '';
      }
      return !message;
    }

    contactData() {
      const value = (name) => (this.form.querySelector(`[data-dx-field="${name}"]`)?.value || '').trim();
      const fullName = value('name').replace(/\s+/g, ' ');
      const [firstName = '', ...rest] = fullName.split(' ');
      return { fullName, firstName, lastName: rest.join(' '), email: value('email'), phone: value('phone'), website: value('website') };
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;
      if (this.firstIncomplete() < this.questions().length) {
        this.goTo(this.firstIncomplete());
        return;
      }

      const fields = [...this.form.querySelectorAll('[data-dx-field]')];
      const invalid = fields.filter((field) => !this.validateField(field));
      if (invalid.length) {
        invalid[0].focus();
        return;
      }
      if (this.designMode) {
        this.showSubmitError('Vista previa del editor', 'Los envíos están desactivados en el editor de temas.');
        return;
      }

      const website = this.form.querySelector('[data-dx-field="website"]');
      if (website && website.value.trim() && !/^https?:\/\//i.test(website.value.trim())) {
        website.value = `https://${website.value.trim()}`;
      }

      const data = this.contactData();
      const route = this.routeConfig();
      const priorities = this.computePriorities();
      const payload = {
        name: data.fullName,
        email: data.email,
        phone: data.phone,
        website: data.website,
        consent: true,
        profile: this.route,
        profileLabel: `${route.name} (${route.code})`,
        answers: this.questions().map((question) => ({ label: question.label, question: question.title, answer: this.answerText(question) })),
        priorities: priorities.map((key) => this.config.priorities[key].title),
        tags: this.leadTags(),
        origin: store.read(ORIGIN_KEY) || {},
        pageUrl: window.location.href.split('#')[0],
        startedAt: this.state.startedAt || 0,
        hp: this.form.querySelector('[data-dx-hp]')?.value || '',
      };

      this.setSubmitting(true);
      this.hideSubmitError();
      let response;
      let body = {};
      try {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 20000);
        response = await window.fetch(this.settings.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        window.clearTimeout(timeout);
        body = await response.json().catch(() => ({}));
      } catch (error) {
        response = null;
      }
      this.setSubmitting(false);

      if (!response || !response.ok || !body.ok) {
        this.handleSubmitError(response, body);
        return;
      }

      store.write(RESULT_KEY, { route: this.route, firstName: data.firstName, priorities, tracked: false, at: Date.now() });
      this.sendToHubSpot(data);
      this.track('diagnostico_envio', { perfil: route.name });
      if (window.location.hash === START_HASH) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      this.showResult();
      this.scrollToTop();
    }

    setSubmitting(active) {
      this.submitting = active;
      const button = this.form.querySelector('[data-dx-submit]');
      const label = button?.querySelector('[data-dx-submit-label]');
      if (!button || !label) return;
      if (!label.dataset.original) label.dataset.original = label.textContent;
      label.textContent = active ? this.settings.submitting || label.dataset.original : label.dataset.original;
      if (active) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }

    handleSubmitError(response, body) {
      if (response && response.status === 422 && Array.isArray(body.fields)) {
        const map = { name: 'name', email: 'email', phone: 'phone', consent: 'consent' };
        const fields = body.fields.map((key) => this.form.querySelector(`[data-dx-field="${map[key]}"]`)).filter(Boolean);
        fields.forEach((field) => {
          field.setAttribute('aria-invalid', 'true');
          const errorEl = document.getElementById(`${field.id}-error`);
          if (errorEl) errorEl.textContent = field.dataset.dxMsg || '';
        });
        if (fields.length) {
          fields[0].focus();
          return;
        }
      }
      const whatsapp = this.settings.whatsapp
        ? ` o <a href="https://wa.me/${this.settings.whatsapp}" target="_blank" rel="noopener">escríbenos por WhatsApp</a>`
        : '';
      if (response && response.status === 429) {
        this.showSubmitError('Recibimos varios envíos seguidos.', `Espera unos minutos e inténtalo otra vez${whatsapp}.`);
      } else {
        this.showSubmitError('No pudimos enviar tu diagnóstico.', `Revisa tu conexión e inténtalo de nuevo${whatsapp}.`);
      }
    }

    showSubmitError(title, html) {
      const alert = this.form.querySelector('[data-dx-submit-error]');
      if (!alert) return;
      alert.querySelector('[data-dx-submit-error-title]').textContent = title;
      alert.querySelector('[data-dx-submit-error-text]').innerHTML = html;
      alert.hidden = false;
      alert.focus();
    }

    hideSubmitError() {
      const alert = this.form.querySelector('[data-dx-submit-error]');
      if (alert) alert.hidden = true;
    }

    sendToHubSpot(data) {
      const portalId = this.settings.hubspotPortalId;
      const formId = this.settings.hubspotFormId;
      if (!portalId || !formId || this.designMode) return;
      if (this.hubspotSentAt && Date.now() - this.hubspotSentAt < 15000) return;
      this.hubspotSentAt = Date.now();

      const route = this.routeConfig();
      const message = this.leadFields()
        .map(([name, value]) => `${name}: ${value}`)
        .join('\n');
      const fields = [
        ['email', data.email],
        ['firstname', data.firstName],
        ['lastname', data.lastName],
        ['phone', data.phone],
        ['website', data.website],
        ['message', message],
      ];
      if (this.settings.hubspotProfileProperty && route) fields.push([this.settings.hubspotProfileProperty, route.name]);

      const hutk = (document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/) || [])[1];
      const payload = {
        fields: fields.filter(([, value]) => value).map(([name, value]) => ({ objectTypeId: '0-1', name, value: String(value) })),
        context: {
          pageUri: window.location.href.split('#')[0],
          pageName: this.settings.pageName,
          ...(hutk ? { hutk } : {}),
        },
        legalConsentOptions: {
          consent: { consentToProcess: true, text: this.settings.consentText },
        },
      };

      try {
        window
          .fetch(
            `https://api.hsforms.com/submissions/v3/integration/submit/${encodeURIComponent(portalId)}/${encodeURIComponent(formId)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
              keepalive: true,
            }
          )
          .catch((error) => console.warn('[Diagnóstico] HubSpot no recibió el lead', error));
      } catch (error) {
        console.warn('[Diagnóstico] HubSpot no recibió el lead', error);
      }
    }

    /* ---------- Resultado */

    showResult({ preview = false } = {}) {
      this.showView('result');
      let result = store.read(RESULT_KEY);
      if (preview && !result) result = { route: Object.keys(this.config.routes)[1], firstName: 'Andrea' };

      const route = result && this.config.routes[result.route];
      const nameEl = this.querySelector('[data-dx-result-name]');
      if (nameEl) nameEl.textContent = result?.firstName ? `, ${result.firstName}` : '';

      if (route) {
        const catalog = this.config.priorities || {};
        const keys = (result.priorities && result.priorities.length ? result.priorities : route.base || [])
          .filter((key) => catalog[key])
          .slice(0, 3);
        this.querySelector('[data-dx-result-route]').textContent = route.name;
        this.querySelector('[data-dx-result-heading]').textContent = route.result?.title || '';
        this.querySelector('[data-dx-result-copy]').textContent = route.result?.text || '';
        this.querySelector('[data-dx-result-priorities]').innerHTML = keys
          .map(
            (key, index) => `
              <li class="dx-prio">
                <span class="dx-prio__num">${index + 1}</span>
                <div>
                  <p class="dx-prio__title">${escapeHtml(catalog[key].title)}</p>
                  <p class="dx-prio__text">${escapeHtml(catalog[key].text)}</p>
                </div>
              </li>`
          )
          .join('');
        this.querySelector('[data-dx-result-profile]').hidden = false;

        const whatsapp = this.querySelector('[data-dx-whatsapp]');
        if (whatsapp && this.settings.whatsapp) {
          const text = `${this.settings.whatsappMessage} Mi perfil: ${route.name}.`.trim();
          whatsapp.href = `https://wa.me/${this.settings.whatsapp}?text=${encodeURIComponent(text)}`;
        }
      }

      if (!preview) {
        store.remove(STATE_KEY);
        this.state = freshState();
        if (result && !result.tracked) {
          this.track('diagnostico_lead', { perfil: route?.name || '' });
          store.write(RESULT_KEY, { ...result, tracked: true });
        }
      }

      window.requestAnimationFrame(() => this.querySelector('[data-dx-result-title]')?.focus({ preventScroll: true }));
    }

    /* ---------- Analítica (GTM / GA4 y píxeles personalizados de Shopify) */

    track(event, detail = {}) {
      if (this.designMode) return;
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...detail });
      } catch (error) {
        /* noop */
      }
      try {
        if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(event, detail);
      } catch (error) {
        /* noop */
      }
    }
  }

  customElements.define('enova-diagnostico', EnovaDiagnostico);
})();
